import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ETAPaymentMethod, isPhonePaymentMethod } from '~/api/ETA';
import { PaymentSystem } from '~/api/Order';
import StripeCardPayment from '~/pages/Checkout/StripeCardPayment';
import { useIsTablet } from '~/pages/Order/hooks';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { cartStore, checkoutStore, mainStore, orderStore } from '~/stores';
import type PaymentMethod from '~/stores/CheckoutStore/PaymentMethod';
import VerifonePayment from '../VerifonePayment';
import AdyenCardPayment from './AdyenCardPayment';

export const handlePaymentsChange = (val: string): void => {
  checkoutStore.setPaymentsMethodId(val);
  checkoutStore.setIsDisabled(false);
  orderStore.setDefaultPayment(val);
  analyticsService.track(AnalyticsEvent.PURCHASE_PAYMENT_METHOD_FILLED_IN, {
    cart_id: undefined,
    payment_method: 'card',
    type: 'existing',
  });
  analyticsService.track(AnalyticsEvent.ADD_PAYMENT_INFO, {
    currency: orderStore.currency.toUpperCase(),
    payment_type: 'credit card',
    value: mainStore.toFloat(cartStore.finalPrice),
    items: cartStore.cartForFirebase,
    coupon: cartStore.promocode.value,
  });
};

export const getOptionDisabledTranslationKey = (
  method: PaymentMethod | null,
): string => {
  if (method === ETAPaymentMethod.Cash) {
    return 'cash';
  }
  if (isPhonePaymentMethod(method as ETAPaymentMethod)) {
    return 'paymentVia';
  }
  if (method === ETAPaymentMethod.QR) {
    return 'mobileMoneyQR';
  }
  return 'bankCard';
};

export const usePaymentMethodInit = (): void => {
  const { t } = useTranslation();
  const isTablet = useIsTablet();

  useEffect(() => {
    if (isTablet && checkoutStore.activePaymentMethod === ETAPaymentMethod.QR) {
      const fallback = checkoutStore.availablePaymentMethods.find(
        (m) => m !== ETAPaymentMethod.QR,
      );
      checkoutStore.setActivePaymentMethod(fallback ?? null, {
        skipAnalytics: true,
      });
      return;
    }

    checkoutStore.initActivePaymentMethod();

    const activePaymentMethod = checkoutStore.activePaymentMethod;

    if (
      activePaymentMethod &&
      !checkoutStore.availablePaymentMethods.includes(activePaymentMethod)
    ) {
      mainStore.pushAlert(
        'error',
        t('checkoutPage:optionDisabled', {
          option: t(getOptionDisabledTranslationKey(activePaymentMethod), {
            operator:
              activePaymentMethod === ETAPaymentMethod.PhoneAirtel
                ? 'Airtel'
                : activePaymentMethod === ETAPaymentMethod.PhoneMoov
                  ? 'Moov'
                  : undefined,
          }),
        }),
      );
    }
  }, [t, checkoutStore.availablePaymentMethods, isTablet]);
};

const AdyenPayment = () => (
  <>
    <div className="adyen__content-payment-add-title-container">
      <AdyenCardPayment />
    </div>
  </>
);

export const CurrentPaymentMethod = observer(() =>
  checkoutStore.activePaymentMethod === ETAPaymentMethod.Card ? (
    checkoutStore.activePaymentSystem === PaymentSystem.adyen ? (
      <AdyenPayment />
    ) : checkoutStore.activePaymentSystem === PaymentSystem.stripe ? (
      <StripeCardPayment />
    ) : checkoutStore.activePaymentSystem === PaymentSystem.verifone ? (
      <VerifonePayment />
    ) : checkoutStore.activePaymentSystem === PaymentSystem.octo ? null : null
  ) : null,
);
